import React from "react";
// react library for routing
import { useLocation, Route, Routes, Navigate } from "react-router-dom";
// core components
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import AdminFooter from "components/Footers/AdminFooter.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import UserDetails from "views/pages/users/userDetails";
import Profile from "views/pages/examples/Profile";
import Conversation from "views/pages/conversation";
import BroadcastForm from "views/pages/broadcasts/BroadcastForm";
import Notifications from "views/pages/notification";
import RolePermission from "views/pages/Role/rolePermission";

import routes from "routes.js";
import { useAuth } from "context/AuthContext";
import { useTranslation } from "context/TranslationProvider";
import OrderDetails from "views/pages/orders/orderDetails";
import ListingDetails from "views/pages/listing/listingDetails/index";
import Setting from "views/pages/setting";
import CreateTemplate from "views/pages/NotificationTemplates/createTemplate/index";
import SupportTicketDetailsPage from "views/pages/support-tickets/SupportTicketDetailsPage";
import VerificationDetailsPage from "views/pages/moderation/VerificationDetailsPage";
import BehavioralDetailsPage from "views/pages/moderation/BehavioralDetailsPage";
import AppealsDetailsPage from "views/pages/moderation/AppealsDetailsPage";
import IpSecurityHistoryPage from "views/pages/login/IpSecurityHistoryPage";
import helpers from "utils/helpers";
import { useNotification } from "providers/NotificationProvider";
import { onForegroundMessage } from "utils/getFcmToken";

function Admin() {
  const [sidenavOpen, setSidenavOpen] = React.useState(true);
  const { t } = useTranslation();
  const location = useLocation();
  const mainContentRef = React.useRef(null);
  const { permission, permissionReady } = useAuth();
  const { notify } = useNotification();
  const memoizedMark = React.useCallback(() => {
    helpers.autoMarkNotificationsForPath(location.pathname);
  }, [location.pathname]);

  React.useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    onForegroundMessage((payload) => {
      if (!isMounted) return;
      const title = payload.notification?.title || payload.data?.title || "New Notification";
      const body = payload.notification?.body || payload.data?.body || "";
      const targetLink = payload.data?.targetLink;

      if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
        try {
          const safeData =
            payload?.data && typeof payload.data === "object"
              ? Object.assign({}, payload.data)
              : {};
          const notif = new Notification(title, {
            body,
            icon: payload.notification?.icon || "/favicon.ico",
            data: safeData,
          });
          if (targetLink && typeof targetLink === "string") {
            try {
              const url = new URL(targetLink, window.location.origin);
              if (url.origin === window.location.origin) {
                notif.onclick = () => {
                  window.focus();
                  window.location.href = url.href;
                };
              }
            } catch (err) {
              console.warn("Invalid notification targetLink:", err);
            }
          }
        } catch (e) {
          console.warn("FCM: Native Notification error:", e);
        }
      }

      if (notify) {
        notify("info", body ? `${title}: ${body}` : title);
      }
    }).then((unsub) => {
      if (!isMounted) {
        if (typeof unsub === "function") {
          unsub();
        }
      } else {
        unsubscribe = unsub;
      }
    });

    return () => {
      isMounted = false;
      if (typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [notify]);

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    if (mainContentRef.current) mainContentRef.current.scrollTop = 0;
    memoizedMark();
  }, [location.pathname, memoizedMark]);

  const getRoutes = (routes) => routes.map((prop, key) => {
    if (prop.collapse) return getRoutes(prop.views);
    if (
      prop.layout === "/admin" &&
      normalizedAllowed.includes(prop.permissionKey?.toLowerCase())
    ) {
      return <Route path={prop.path} element={prop.component} key={key} />;
    }
    return null;
  });

  const getBrandText = () => {
    for (let i = 0; i < routes.length; i++) {
      if (location.pathname.indexOf(routes[i].layout + routes[i].path) !== -1) {
        return t(routes[i].name);
      }
    }
    return "Brand";
  };

  const toggleSidenav = () => {
    if (document.body.classList.contains("g-sidenav-pinned")) {
      document.body.classList.remove("g-sidenav-pinned");
      document.body.classList.add("g-sidenav-hidden");
    } else {
      document.body.classList.add("g-sidenav-pinned");
      document.body.classList.remove("g-sidenav-hidden");
    }
    setSidenavOpen(!sidenavOpen);
  };
  const getNavbarTheme = () => {
    return location.pathname.indexOf("admin/alternative-dashboard") === -1
      ? "dark"
      : "light";
  };
  
  if (!permissionReady) {
    return (
      <div className="text-center mt-5">
        {t("common.loading", "Loading...")}
      </div>
    );
  }

  const normalizedAllowed = permission.allowedRoutes.map(p =>
    p.toLowerCase().replace(/\s+/g, "")
  );

  const filteredRoutes = routes
  .map((route) => {
    if (route.collapse) {
      const allowedViews = route.views.filter((view) =>
        normalizedAllowed.includes(view.permissionKey?.toLowerCase())
      );
      if (allowedViews.length > 0) {
        return { ...route, views: allowedViews };
      }
      return null;
    } else if (normalizedAllowed.includes(route.permissionKey?.toLowerCase())) {
      return route;
    }
    return null;
  })
  .filter(Boolean);

  return (
    <>
      <Sidebar
        routes={filteredRoutes}
        toggleSidenav={toggleSidenav}
        sidenavOpen={sidenavOpen}
        logo={{
          innerLink: "/",
          imgSrc: require("assets/img/images/logo.png"),
          imgAlt: "...",
        }}
      />
      <div className="main-content" ref={mainContentRef}>
        <AdminNavbar
          theme={getNavbarTheme()}
          toggleSidenav={toggleSidenav}
          sidenavOpen={sidenavOpen}
          brandText={getBrandText()}
        />
        <Routes>
          {getRoutes(routes)}
          <Route
            path="*"
            element={<Navigate to="/admin/dashboard" replace />}
          />
          <Route
            path="support-tickets/:id/details"
            element={<SupportTicketDetailsPage />}
          />
          <Route
            path="moderation/verification/:flagId/details"
            element={<VerificationDetailsPage />}
          />
          <Route
            path="moderation/behavioral/:flagId/details"
            element={<BehavioralDetailsPage />}
          />
          <Route
            path="moderation/appeals/:appealId/details"
            element={<AppealsDetailsPage />}
          />
          <Route
            path="users/userDetails/:userId"
            element={<UserDetails />}
          />
          <Route
            path="broadcasts/create"
            element={<BroadcastForm />}
          />
          <Route
            path="broadcasts/edit/:broadcastId"
            element={<BroadcastForm />}
          />
          <Route
            path="all-orders/orderDetails/:orderId"
            element={<OrderDetails />}
          />
          <Route
            path="/listing-details/:type/:listingId"
            element={<ListingDetails />}
          />
          <Route
            path="conversation/:conversationType/:conversationId"
            element={<Conversation />}
          />
          <Route
            path="profile"
            element={<Profile />}
          />
          <Route
            path="notification"
            element={<Notifications />}
          />
          <Route
            path="/role/:roleId"
            element={<RolePermission />}
          />
          <Route
            path="/role/new"
            element={<RolePermission />}
          />
          <Route
            path="setting"
            element={<Setting />}
          />
          <Route
            path="notification-templates/create"
            element={<CreateTemplate />}
          />

          <Route
            path="failedLogin/details/:ipAddress"
            element={<IpSecurityHistoryPage />}
          />
          <Route
            path="failedLogin/:ipAddress/history"
            element={<IpSecurityHistoryPage />}
          />
          <Route
            path="notification-templates/edit/:templateId"
            element={<CreateTemplate />}
          />
        </Routes>
        <AdminFooter />
      </div>
      {sidenavOpen && (
        <div className="backdrop d-xl-none" onClick={toggleSidenav} />
      )}
    </>
  );
}

export default Admin;
